import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import EmptyDropdownMessage from 'src/components/EmptyDropdownMessage/EmptyDropdownMessage'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBoxBottom from 'src/components/LoadingBoxBottom/LoadingBoxBottom'
import SoftAlertBox from 'src/components/SoftAlertBox/SoftAlertBox'
import SvgCheckLoD from 'src/components/Svgs/SvgCheckLoD'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import {
  useDebounced,
  useDropdownOpen,
  useElementScrollBottomHandler,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import variables from 'src/style/variables.json'
import { setEquality } from 'src/utils/set.utils'
import styled, { css, CSSProp } from 'styled-components'
export interface DropdownOptionGroup<M extends DropdownOption<unknown>> {
  label?: React.ReactNode
  options: M[]
  hideGroup?: boolean
}

export interface DropdownOption<N> {
  value: string
  label?: string
  details?: N
  disabled?: boolean
}

export interface DropdownItemProps<N> {
  className?: string
  isSelected: boolean
  isRoot: boolean
  details?: N
  value: string
  disabled?: boolean
  isUpdated?: boolean
  chooseOption?: (val: string) => void
  isMulti?: boolean
  id?: string
  minimalDisplay?: boolean
  dropdownV2?: boolean
}

export interface MultiItemProps<N> {
  className?: string
  options: Array<DropdownOption<N>>
  chooseOption?: (val: string) => void
}

export interface MultiItemPillProps<N> {
  className?: string
  options: Array<DropdownOption<N>>
  chooseOption?: (val: string) => void
  selectedIndex?: number
  setSelectedIndex: (val: number | undefined) => void
}

export interface DropdownInfiniteScrollProps {
  fetch: (filterInput?: string, page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
}

interface DropdownProps<N> {
  allowNoneProps?: {
    NoneDropdownItem: React.FC<{ className?: string }>
    deselectAllOptions: () => void
  }
  baseValues?: Set<string>
  chooseOption: (val: string) => void
  chooseMultiOption?: (val: string[]) => void
  className?: string
  customEmptyDropdownMessage?: React.ReactElement
  customNoMatchingOptionsDropdownMessage?: React.ReactElement
  DropdownItem: React.FC<DropdownItemProps<N>>
  errors?: Array<React.ReactElement | string>
  fieldLabel?: React.ReactNode | string
  fieldLabelTestId?: string
  fieldLabelAriaLabel?: string
  filter?: (option: DropdownOption<N>, val: string) => boolean
  getFilterText: (option: DropdownOption<N>) => string | undefined
  isMulti?: boolean
  showSuggested?: boolean
  isOptional?: boolean
  MultiItem?: React.FC<MultiItemProps<N>>
  MultiItemPills?: React.FC<MultiItemPillProps<N>>
  newOptionLabel?: React.ReactNode
  externalReset?: {
    resetClicked: boolean
    setResetClicked: (value: boolean) => void
  }
  onResetClick?: () => void
  optionGroups: Array<DropdownOptionGroup<DropdownOption<N>>>
  recentOptionGroups?: Array<DropdownOptionGroup<DropdownOption<N>>>
  placeholder?: string
  promo?: React.ReactElement
  selectedValues: Set<string>
  showUpdates?: boolean
  hideWasOnUpdate?: boolean
  isStatic?: boolean
  selectedOptions?: Array<DropdownOption<N>>
  validateNewOption?: (val: string) => boolean
  infiniteScrollProps?: DropdownInfiniteScrollProps
  tooltip?: React.ReactElement
  hideToolipOnUpdate?: boolean
  displayPills?: boolean
  setShowMessageCard?: (value: boolean) => void
  disabled?: boolean
  inputDisabled?: boolean
  smallDisplay?: boolean
  rootContainerCss?: CSSProp
  id?: string
  'data-testid'?: string
  testIdOptionBox?: string
  minimalDisplay?: boolean
  showOutsideOrgAlert?: React.ReactNode
  errorBorder?: boolean
  countryPhoneDropdown?: boolean
  startAndKeepOpen?: boolean
  embeddedStyle?: boolean
}

const StyledInput = styled.input<{
  visible: boolean
  isMulti?: boolean
  inputDisabled?: boolean
  id?: string
}>`
  flex-grow: 2;
  height: 100%;
  caret-color: ${themeConstants.colors.cta};
  border: none;
  cursor: pointer;
  max-height: 230px;
  overflow: scroll;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )}

  background-color: transparent;
  &::placeholder {
    opacity: 0.5;
    ${getFontCss(
      14,
      styleConstants.colors.gray_darkest,
      styleConstants.fontFamilies.regular,
    )}
  }

  opacity: 0;
  max-width: 0px;

  ${({ visible }): CSSProp => {
    return visible
      ? css`
          opacity: 1;
          max-width: none;
        `
      : css``
  }}

  ${({ inputDisabled }): CSSProp => {
    return !inputDisabled
      ? css`
          cursor: text;
        `
      : css``
  }}
`

// const regularValueToOptionMap: Record<string, DropdownOption<unknown>> = {}
const Dropdown = <_M extends string | number, N>(
  props: DropdownProps<N>,
): React.ReactNode => {
  const translate = useTranslate()

  const selectedValuesArray = useMemo(
    (): string[] => Array.from(props.selectedValues),
    [props.selectedValues],
  )

  // filter hooks
  const [filterInput, setFilterInput] = useState<string>('')
  const [isFilterActive, setIsFilterActive] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [showAlert, setShowAlert] = useState<boolean>(true)

  const [selectedCoordinates, setSelectedCoordinates] = useState<{
    groupIndex: number
    elementIndex: number
  }>({
    groupIndex: 0,
    elementIndex: 0,
  })

  // dropdown open/close hooks
  const { dropdownOpen, divRef, handleOpenerClick, setDropdownOpen } =
    useDropdownOpen({ startAndKeepOpen: props.startAndKeepOpen })

  const onOpenerClick = (e: React.SyntheticEvent): void => {
    props.setShowMessageCard && props.setShowMessageCard(false)
    if (!props.disabled) {
      handleOpenerClick(e)
      setShowAlert(true)
      if (!dropdownOpen) {
        inputRef.current?.focus()
      }
    }
  }
  const onFilterInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    e.preventDefault()
    setFilterInput(e.target.value)
    setSelectedCoordinates({ groupIndex: 0, elementIndex: 0 })
    setIsFilterActive(e.target.value !== '')
  }

  const [dropdownOptionsRef] = useAutoAnimate({
    duration: 90,
    easing: 'ease-in',
  })

  const [highlightedPillIndex, setHighlightedPillIndex] = useState<
    number | undefined
  >()

  const { debouncedF } = useDebounced((f?: string, p: number = 0): void => {
    props.infiniteScrollProps?.fetch(f, p)
  })

  // track whether the user actually scrolled after opening
  const userScrolledRef = useRef(false)
  const lastScrollTopRef = useRef(0)
  useEffect(() => {
    // reset on every open/close toggle
    userScrolledRef.current = false
    lastScrollTopRef.current = 0
  }, [dropdownOpen])

  const hasEverOpenedRef = useRef(false)

  useEffect(() => {
    if (dropdownOpen) hasEverOpenedRef.current = true
  }, [dropdownOpen])

  const lastInitialQueryRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!dropdownOpen || !props.infiniteScrollProps) return

    const query = isFilterActive ? filterInput : undefined
    const shouldFetchInitialPage =
      // first time opening with no data
      props.infiniteScrollProps.page == null ||
      // or query text actually changed
      lastInitialQueryRef.current !== query

    if (shouldFetchInitialPage) {
      lastInitialQueryRef.current = query
      debouncedF(query, 0)
    }
  }, [
    dropdownOpen,
    isFilterActive,
    filterInput,
    debouncedF,
    props.infiniteScrollProps?.page,
  ])

  const suppressFirstScrollAfterOpen = useRef(false)
  useEffect(() => {
    if (dropdownOpen) suppressFirstScrollAfterOpen.current = true
  }, [dropdownOpen])

  const handleScroll = useCallback((): void => {
    if (!userScrolledRef.current) return
    if (suppressFirstScrollAfterOpen.current) {
      suppressFirstScrollAfterOpen.current = false
      return
    }

    if (
      props.infiniteScrollProps !== undefined &&
      !props.infiniteScrollProps?.loading &&
      !props.infiniteScrollProps?.complete
    ) {
      props.infiniteScrollProps?.fetch(
        isFilterActive ? filterInput : undefined,
        props.infiniteScrollProps?.page !== undefined
          ? props.infiniteScrollProps?.page + 1
          : 0,
      )
    }
  }, [
    props.infiniteScrollProps?.loading,
    props.infiniteScrollProps?.complete,
    props.infiniteScrollProps?.page,
    props.infiniteScrollProps?.fetch,
    isFilterActive,
    filterInput,
  ])
  const { onScroll, childRef, parentRef } =
    useElementScrollBottomHandler(handleScroll)
  // external action to reset input field & dropdown
  useEffect((): void => {
    if (props.externalReset?.resetClicked) {
      setFilterInput('')
      props.externalReset?.setResetClicked(false)
    }
  }, [props.externalReset?.resetClicked])

  // helper values
  // once an option is in this map, do not ever remove it, even on re-fetch, assumes same pool
  const regularValueToOptionMapRef = useRef<Record<string, DropdownOption<N>>>(
    Object.create(null) as Record<string, DropdownOption<N>>,
  )
  const regularValueToOptionMap: Record<
    string,
    DropdownOption<N>
  > = useMemo(() => {
    const map = regularValueToOptionMapRef.current

    for (const group of props.optionGroups) {
      for (const option of group.options) {
        map[option.value] = option
      }
    }

    for (const option of props.selectedOptions ?? []) {
      map[option.value] = option
    }

    return map
  }, [props.optionGroups, props.selectedOptions])

  const { selectedRegularOptions, selectedNewOptions } = useMemo<{
    selectedRegularOptions: Array<DropdownOption<N>>
    selectedNewOptions: Array<DropdownOption<N>>
  }>(() => {
    const selectedRegular: Array<DropdownOption<N>> = []
    const selectedNew: Array<DropdownOption<N>> = []

    for (const selectedValue of selectedValuesArray) {
      const option = regularValueToOptionMap[selectedValue]
      if (option !== undefined) {
        selectedRegular.push(option)
      } else {
        selectedNew.push({ value: selectedValue })
      }
    }

    return {
      selectedRegularOptions: selectedRegular,
      selectedNewOptions: selectedNew,
    }
  }, [selectedValuesArray, regularValueToOptionMap])

  const selectedAllOptions = [...selectedRegularOptions, ...selectedNewOptions]

  useEffect((): void => {
    if (!dropdownOpen && !props.isMulti && selectedValuesArray.length === 0) {
      setFilterInput('')
    }
  }, [selectedValuesArray.length, dropdownOpen])

  useEffect((): void => {
    if (!dropdownOpen) {
      setIsFilterActive(false)
      if (selectedAllOptions.length > 0 && !props.isMulti) {
        setFilterInput(props.getFilterText(selectedAllOptions[0]) ?? '')
      } else {
        setFilterInput('')
      }
    }
  }, [dropdownOpen, selectedAllOptions])

  const potentialNewOptions: Array<DropdownOption<N>> =
    filterInput &&
    regularValueToOptionMap[filterInput] === undefined &&
    !props.selectedValues?.has(filterInput) &&
    props.validateNewOption !== undefined &&
    props.validateNewOption(filterInput) &&
    isFilterActive &&
    !props.infiniteScrollProps?.loading // && not loading
      ? [{ value: filterInput }]
      : []

  const selectedAndPotentialNewOptions = useMemo(
    () => [...potentialNewOptions, ...selectedNewOptions],
    [potentialNewOptions, selectedNewOptions],
  )

  const noneOptionOptionGroup: Array<DropdownOptionGroup<DropdownOption<N>>> =
    props.allowNoneProps !== undefined
      ? [
          {
            options: [
              {
                value: '', // TODO: is empty string really the best way to do this without breaking interfaces?
              },
            ],
          },
        ]
      : []
  const newOptionOptionGroup: Array<DropdownOptionGroup<DropdownOption<N>>> =
    selectedAndPotentialNewOptions.length > 0
      ? [
          {
            label: props.newOptionLabel,
            options: selectedAndPotentialNewOptions,
          },
        ]
      : []

  const recentOptionOptionGroup = props.recentOptionGroups || []

  const allOptionGroups = useMemo<
    Array<DropdownOptionGroup<DropdownOption<N>>>
  >(() => {
    const groups: Array<DropdownOptionGroup<DropdownOption<N>>> = []

    for (const group of noneOptionOptionGroup) groups.push(group)
    for (const group of newOptionOptionGroup) groups.push(group)
    for (const group of recentOptionOptionGroup) groups.push(group)
    for (const group of props.optionGroups) groups.push(group)

    return groups
  }, [
    noneOptionOptionGroup,
    newOptionOptionGroup,
    recentOptionOptionGroup,
    props.optionGroups,
  ])

  const filteredOptionGroups = useMemo(() => {
    if (!(isFilterActive && props.filter !== undefined)) {
      return allOptionGroups
    }

    const groups: Array<DropdownOptionGroup<DropdownOption<N>>> = []

    for (const group of allOptionGroups) {
      if (group.hideGroup) continue

      const filteredOptions: Array<DropdownOption<N>> = []
      for (const option of group.options) {
        if (props.filter?.(option, filterInput)) {
          filteredOptions.push(option)
        }
      }

      if (filteredOptions.length > 0) {
        groups.push({
          label: group.label,
          options: filteredOptions,
          hideGroup: group.hideGroup,
        })
      }
    }

    return groups
  }, [isFilterActive, props.filter, allOptionGroups, filterInput])

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const onOptionClick = (
    val: string | string[],
    e?: React.MouseEvent,
  ): void => {
    e?.stopPropagation()
    setShowErrors(false)
    setFilterInput('')
    if (props.allowNoneProps !== undefined && val === '') {
      props.allowNoneProps.deselectAllOptions()
    } else {
      if (Array.isArray(val) && props.chooseMultiOption) {
        props.chooseMultiOption(val)
      }
      if (typeof val === 'string') {
        props.chooseOption(val)
      }
    }
    if (!props.isMulti) {
      setDropdownOpen(false)
    } else {
      inputRef.current?.focus()
    }
  }

  const onOptionMouseOver = (i: number, j: number): void => {
    setSelectedCoordinates({
      groupIndex: i,
      elementIndex: j,
    })
  }

  const onFilterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      inputRef.current?.blur()
      setDropdownOpen(false)
    }
    if (e.key === 'Tab') {
      if (dropdownOpen) {
        onUserActionSelectOption()
        setDropdownOpen(false)
      }
    } else if (!dropdownOpen) {
      setDropdownOpen(true)
    }
    if (e.key === 'ArrowUp' && filteredOptionGroups.length) {
      if (selectedCoordinates.elementIndex - 1 >= 0) {
        setSelectedCoordinates({
          groupIndex: selectedCoordinates.groupIndex,
          elementIndex: selectedCoordinates.elementIndex - 1,
        })
      } else if (selectedCoordinates.groupIndex - 1 >= 0) {
        setSelectedCoordinates({
          groupIndex: selectedCoordinates.groupIndex - 1,
          elementIndex:
            filteredOptionGroups[selectedCoordinates.groupIndex - 1].options
              .length - 1,
        })
      }
    }
    if (e.key === 'ArrowDown' && filteredOptionGroups.length) {
      inputRef.current?.focus()
      if (
        selectedCoordinates.elementIndex + 1 <
        filteredOptionGroups[selectedCoordinates.groupIndex].options.length
      ) {
        setSelectedCoordinates({
          groupIndex: selectedCoordinates.groupIndex,
          elementIndex: selectedCoordinates.elementIndex + 1,
        })
      } else if (
        selectedCoordinates.groupIndex + 1 <
        filteredOptionGroups.length
      ) {
        setSelectedCoordinates({
          groupIndex: selectedCoordinates.groupIndex + 1,
          elementIndex: 0,
        })
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      onUserActionSelectOption()
      setDropdownOpen(false)
    }
    if (e.key === ';' || e.key === ',') {
      e.preventDefault()
      onUserActionSelectOption()
    }
  }

  const onUserActionSelectOption = (): void => {
    if (
      !selectedValuesArray.includes(
        filteredOptionGroups[selectedCoordinates.groupIndex]?.options[
          selectedCoordinates.elementIndex
        ].value,
      )
    ) {
      onOptionClick(
        filteredOptionGroups[selectedCoordinates.groupIndex]?.options[
          selectedCoordinates.elementIndex
        ].value,
      )
    }
  }

  const onMultiFilterKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    onFilterKeyDown(e)
    if (e.key === 'Backspace') {
      if (
        inputRef.current?.value === '' &&
        highlightedPillIndex === undefined
      ) {
        e.preventDefault()
        setHighlightedPillIndex(selectedAllOptions.length - 1)
      } else if (highlightedPillIndex !== undefined) {
        e.preventDefault()
        const selectedEmail =
          selectedAllOptions.filter((val, i) => i === highlightedPillIndex)[0]
            ?.value || null
        selectedEmail ? props.chooseOption(selectedEmail) : undefined
        setHighlightedPillIndex(undefined)
      }
    }
  }

  const onSingleFilterPaste = (
    e: React.ClipboardEvent<HTMLDivElement>,
  ): void => {
    const input = e.clipboardData.getData('text').trim()
    if (props.validateNewOption && props.validateNewOption(input)) {
      onOptionClick(input)
      e.preventDefault()
    }
  }

  const onMultiFilterPaste = (
    e: React.ClipboardEvent<HTMLDivElement>,
  ): void => {
    onOptionClick(
      e.clipboardData
        .getData('text')
        .split(/,|\s|;/)
        .filter(
          (input) =>
            props.validateNewOption && props.validateNewOption(input.trim()),
        ),
    )
    e.preventDefault()
  }

  // updated
  const isUpdated = useMemo(
    () =>
      !!props.showUpdates &&
      props.baseValues !== undefined &&
      !setEquality(props.baseValues, props.selectedValues),
    [props.showUpdates, props.baseValues, props.selectedValues],
  )
  const firstBaseValue = useMemo(
    () => props.baseValues?.values().next().value as string | undefined,
    [props.baseValues],
  )
  const firstBaseOption: DropdownOption<N> | undefined = useMemo(() => {
    if (firstBaseValue === undefined) return undefined
    return (
      regularValueToOptionMap[firstBaseValue] || {
        value: firstBaseValue,
      }
    )
  }, [firstBaseValue, regularValueToOptionMap])

  const optionId = props.id ? `${props.id}DropList` : ''
  const optionTestId = props['data-testid'] || props.id || ''

  return (
    <div
      className={props.className}
      css={css`
        position: relative;
        display: flex;
        flex-direction: column;
        ${props.embeddedStyle &&
        css`
          background-color: ${variables.tokensColor.white100};
          padding: ${variables.units['base-unit-5']} 0px;
        `}
      `}
    >
      {props.fieldLabel !== undefined && (
        <div
          css={css`
            display: flex;
            align-items: center;
            word-wrap: break-word;
          `}
        >
          <FieldLabel
            css={css`
              z-index: 1;
              max-height: 30px;
            `}
            isUpdated={isUpdated}
            testId={props.fieldLabelTestId}
            ariaLabel={props.fieldLabelAriaLabel}
          >
            {!isUpdated ||
            firstBaseOption === undefined ||
            props.hideWasOnUpdate ? (
              <Fragment>
                {!props.isOptional && props.fieldLabel}
                {props.isOptional &&
                  translate('${fieldLabel} (optional)', {
                    fieldLabel: props.fieldLabel,
                  })}
              </Fragment>
            ) : (
              <Fragment>
                {!props.isOptional &&
                  translate('${fieldLabel} (was "${baseValue}")', {
                    fieldLabel: props.fieldLabel,
                    baseValue: props.getFilterText(firstBaseOption) ?? '',
                  })}
                {props.isOptional &&
                  translate('${fieldLabel} (optional) (was "${baseValue}")', {
                    fieldLabel: props.fieldLabel,
                    baseValue: props.getFilterText(firstBaseOption) ?? '',
                  })}
              </Fragment>
            )}
          </FieldLabel>
          {props.tooltip !== undefined &&
            (!isUpdated || (isUpdated && !props.hideToolipOnUpdate)) &&
            props.tooltip}
        </div>
      )}
      {props.isStatic &&
        props.selectedOptions !== undefined &&
        props.selectedOptions.length > 0 && (
          <div
            css={css`
              height: 50px;
            `}
          >
            {!props.isMulti && (
              <props.DropdownItem
                isRoot={true}
                isSelected={false}
                details={props.selectedOptions[0].details}
                value={props.selectedOptions[0].value}
                disabled={props.selectedOptions[0].disabled}
                chooseOption={
                  !props.selectedOptions[0].disabled
                    ? props.chooseOption
                    : undefined
                }
                minimalDisplay={props.minimalDisplay}
              />
            )}
            {props.isMulti && props.MultiItem !== undefined && (
              <props.MultiItem
                options={props.selectedOptions}
                chooseOption={props.chooseOption}
              />
            )}
          </div>
        )}
      {!props.isStatic && (
        <Fragment>
          <div
            css={css`
              position: relative;
              max-height: 230px;
              ${props.countryPhoneDropdown &&
              css`
                min-width: 130px;
              `}
              ${(props.disabled || false) &&
              css`
                opacity: 0.5;
                pointer-events: none;
              `}
            `}
            ref={divRef}
          >
            <div
              data-testid={
                props['data-testid']
                  ? `${props['data-testid']}-textbox`
                  : typeof props.fieldLabel === 'string'
                    ? `${props.fieldLabel.toLowerCase().replace(/\s+/g, '-')}-textbox`
                    : props.id
                      ? `${props.id.toLowerCase().replace(/\s+/g, '-')}-textbox`
                      : ''
              }
              css={css`
                position: relative;
                display: flex;
                align-items: center;
                justify-content: space-between;
                min-height: 50px;
                max-height: 230px;
                border: 1px solid ${styleConstants.colors.gray_medium};
                padding: 0px 10px 0px 20px;
                border-radius: 6px;
                cursor: pointer;
                background-color: ${styleConstants.colors.gray_lightest};

                ${!!props.errorBorder &&
                css`
                  border: 1px solid ${styleConstants.colors.red};
                `}

                ${dropdownOpen &&
                !props.embeddedStyle &&
                css`
                  background-color: ${styleConstants.colors.white};
                  border: 1px solid ${themeConstants.colors.cta};
                `}

                ${!props.disabled &&
                css`
                  &:focus-within,
                  &:hover {
                    background-color: ${styleConstants.colors.white};
                    border: 1px solid ${themeConstants.colors.cta};
                  }
                `}

                ${!!props.isMulti &&
                css`
                  cursor: text;

                  &:focus-within,
                  &:hover {
                    background-color: ${styleConstants.colors.gray_lightest};
                    border: 1px solid ${styleConstants.colors.gray_medium};
                  }
                `}

                ${isUpdated &&
                css`
                  border: 1px solid ${themeConstants.colors.highlight};

                  &:focus-within,
                  &:hover {
                    border: 1px solid ${themeConstants.colors.highlight};
                  }
                `}

                ${showErrors &&
                css`
                  border: 1px solid ${styleConstants.colors.red};

                  &:focus-within,
                  &:hover {
                    border: 1px solid ${styleConstants.colors.red};
                  }
                `}

                ${!!props.inputDisabled &&
                css`
                  cursor: pointer;
                `}

                ${props.rootContainerCss}
              `}
              onClick={onOpenerClick}
              onKeyPress={(): null => {
                return null
              }}
              role="button"
              tabIndex={0}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  overflow: auto;
                  flex-grow: 1;
                  max-height: 230px;
                  padding-top: 15px;
                  padding-bottom: 15px;
                `}
              >
                {props.isMulti &&
                props.displayPills &&
                props.MultiItemPills !== undefined ? (
                  <div
                    css={css`
                      align-items: center;
                      display: flex;
                      position: relative;
                      flex-wrap: wrap;
                      flex-grow: 1;
                    `}
                    role="button"
                    onKeyDown={onMultiFilterKeyDown}
                    tabIndex={0}
                  >
                    <props.MultiItemPills
                      options={selectedAllOptions}
                      selectedIndex={highlightedPillIndex}
                      setSelectedIndex={setHighlightedPillIndex}
                    />
                    <StyledInput
                      id={props.id ? props.id + 'Field' : 'Field'}
                      disabled={props.inputDisabled}
                      inputDisabled={props.inputDisabled}
                      size={1}
                      ref={inputRef}
                      value={filterInput}
                      onChange={onFilterInputChange}
                      onPaste={onMultiFilterPaste}
                      placeholder={dropdownOpen ? '' : props.placeholder}
                      onFocus={(e): void => {
                        e.target.select()
                      }}
                      role={'presentation'}
                      autoComplete="off"
                      visible={dropdownOpen || selectedAllOptions.length === 0}
                      isMulti={!!props.isMulti}
                      data-testid={
                        props['data-testid']
                          ? `${props['data-testid']}-textbox`
                          : typeof props.fieldLabel === 'string'
                            ? `${props.fieldLabel.toLowerCase().replace(/\s+/g, '-')}-textbox`
                            : props.id
                              ? `${props.id.toLowerCase().replace(/\s+/g, '-')}-textbox`
                              : ''
                      }
                    />
                  </div>
                ) : (
                  <StyledInput
                    size={1}
                    id={props.id ? props.id + 'Field' : 'Field'}
                    disabled={props.inputDisabled}
                    inputDisabled={props.inputDisabled}
                    ref={inputRef}
                    value={filterInput}
                    onPaste={onSingleFilterPaste}
                    onChange={onFilterInputChange}
                    onKeyDown={onFilterKeyDown}
                    placeholder={dropdownOpen ? '' : props.placeholder}
                    onFocus={(e): void => {
                      e.target.select()
                    }}
                    role={'presentation'}
                    autoComplete="off"
                    visible={dropdownOpen || selectedAllOptions.length === 0}
                    data-testid={
                      props['data-testid']
                        ? `${props['data-testid']}-input`
                        : typeof props.fieldLabel === 'string'
                          ? `${props.fieldLabel.toLowerCase().replace(/\s+/g, '-')}-input`
                          : props.id
                            ? `${props.id.toLowerCase().replace(/\s+/g, '-')}-input`
                            : ''
                    }
                  />
                )}
                {!dropdownOpen &&
                  selectedAllOptions.length > 0 &&
                  !props.isMulti && (
                    <props.DropdownItem
                      id={props.id ? props.id + 'CreditCards' : 'CreditCards'}
                      isRoot={true}
                      isSelected={false}
                      isUpdated={isUpdated}
                      details={selectedAllOptions[0].details}
                      value={selectedAllOptions[0].value}
                      disabled={selectedAllOptions[0].disabled}
                      chooseOption={
                        !selectedAllOptions[0].disabled
                          ? props.chooseOption
                          : undefined
                      }
                      minimalDisplay={props.minimalDisplay}
                    />
                  )}
                {!dropdownOpen &&
                  selectedAllOptions.length > 0 &&
                  props.isMulti &&
                  !props.displayPills &&
                  props.MultiItem !== undefined && (
                    <props.MultiItem
                      options={selectedAllOptions}
                      chooseOption={props.chooseOption}
                    />
                  )}
              </div>
              <span
                css={css`
                  cursor: pointer;
                  flex-shrink: 0;

                  ${!!props.smallDisplay &&
                  css`
                    width: 24px;
                  `}
                `}
                onClick={(): void => {
                  setDropdownOpen(!dropdownOpen)
                }}
                onKeyPress={(): null => {
                  return null
                }}
                role={'button'}
                tabIndex={-1}
                aria-label={'click area'}
                id={props.id ? props.id + 'Arrow' : 'Arrow'}
                data-testid={
                  props['data-testid']
                    ? `${props['data-testid']}-arrow`
                    : typeof props.fieldLabel === 'string'
                      ? `${props.fieldLabel.toLowerCase().replace(/\s+/g, '-')}-arrow`
                      : props.id
                        ? `${props.id.toLowerCase().replace(/\s+/g, '-')}-arrow`
                        : ''
                }
                data-isopen={dropdownOpen}
              >
                {!dropdownOpen ? (
                  <SvgChevronDown
                    css={css`
                      margin-left: 10px;
                      cursor: pointer;
                      float: right;

                      ${!!props.smallDisplay &&
                      css`
                        transform: scale(0.583);
                      `}
                    `}
                    id={props.id ? props.id + 'Arrow' : 'Arrow'}
                  />
                ) : (
                  <SvgChevronUp
                    css={css`
                      margin-left: 10px;
                      cursor: pointer;
                      float: right;
                      ${!!props.smallDisplay &&
                      css`
                        transform: scale(0.583);
                      `}
                    `}
                  />
                )}
              </span>
            </div>
            <div ref={dropdownOptionsRef}>
              {dropdownOpen && (
                <div
                  data-testid={props.testIdOptionBox}
                  css={css`
                    ${props.countryPhoneDropdown &&
                    css`
                      align-items: center;
                    `}
                    min-width: ${props.showOutsideOrgAlert !== undefined
                      ? css`calc(100% + 125px)`
                      : css`100%`};
                    max-width: 100%;
                    z-index: 3;
                    position: absolute;
                    top: calc(100% + 5px);
                    border-radius: 6px;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    border: 1px solid ${themeConstants.colors.cta};
                    ${props.embeddedStyle &&
                    css`
                      border: 1px solid transparent;
                      padding: ${variables.units['base-unit-5']} 0px;
                      scrollbar-width: 25px;
                    `}
                    background-color: ${styleConstants.colors.white};

                    ${!dropdownOpen &&
                    css`
                      opacity: 0;
                    `}

                    ${!!props.smallDisplay &&
                    css`
                      min-width: max-content;
                    `}

                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                      width: 100%;
                    `)}
                  `}
                >
                  {props.promo !== undefined && props.promo}
                  <div
                    data-testid={`${props['data-testid'] ?? props.id ?? ''}-listbox`}
                    css={css`
                      overflow: auto;
                      max-height: 230px;
                      min-height: 64px;
                    `}
                    onScroll={onScroll}
                    onScrollCapture={() => {
                      userScrolledRef.current = true
                    }}
                    ref={parentRef}
                  >
                    <div ref={childRef}>
                      {potentialNewOptions.length === 0 &&
                        !filteredOptionGroups.some(
                          (group) => group.options.length > 0,
                        ) &&
                        (props.infiniteScrollProps === undefined ||
                          props.infiniteScrollProps.complete) &&
                        (filterInput === '' ? (
                          // no options at all
                          <>
                            {props.customEmptyDropdownMessage === undefined && (
                              <EmptyDropdownMessage /> // TODO consider updating messaging in this scenario
                            )}
                            {props.customEmptyDropdownMessage !== undefined &&
                              props.customEmptyDropdownMessage}
                          </>
                        ) : (
                          // no options matching the filter
                          <>
                            {props.customNoMatchingOptionsDropdownMessage ===
                              undefined && <EmptyDropdownMessage />}
                            {props.customNoMatchingOptionsDropdownMessage !==
                              undefined &&
                              props.customNoMatchingOptionsDropdownMessage}
                          </>
                        ))}
                      {showAlert && props.showOutsideOrgAlert && (
                        <SoftAlertBox
                          css={css`
                            border-radius: 6px;
                            margin-top: 28px;
                            padding: 12px 20px;
                            margin-left: 20px;
                            margin-right: 20px;
                          `}
                        >
                          <span
                            css={css`
                              ${getFontCss(
                                12,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.regular,
                              )};
                            `}
                          >
                            {props.showOutsideOrgAlert}
                          </span>
                        </SoftAlertBox>
                      )}
                      {filteredOptionGroups.map((optionGroup, i) => (
                        <div
                          data-testid={`${props['data-testid']}-listbox`}
                          key={i}
                          css={css`
                            display: flex;
                            flex-direction: column;
                          `}
                          id={props.id ? props.id + 'Dropdown' : 'Dropdown'}
                        >
                          {optionGroup.label !== undefined && (
                            <span
                              css={css`
                                padding: 20px 44px 6px;
                                background-color: ${styleConstants.colors
                                  .white};
                                width: 100%;
                                ${getFontCss(
                                  14,
                                  styleConstants.colors.gray_darkest,
                                  styleConstants.fontFamilies.regular,
                                )}
                              `}
                            >
                              {optionGroup.label}
                            </span>
                          )}
                          {optionGroup.options.map(
                            (option, j): React.ReactNode => (
                              <div
                                id={optionId}
                                data-testid={`${optionTestId}-option-${j}`}
                                data-label={option?.label}
                                data-name={props.getFilterText(option) ?? ''}
                                css={css`
                                  flex-shrink: 0;
                                  display: flex;
                                  align-items: center;
                                  cursor: pointer;
                                  padding: 0px 20px 0px 20px;

                                  ${option.disabled &&
                                  css`
                                    opacity: 0.5;
                                    cursor: default;
                                  `}

                                  background-color: ${styleConstants.colors
                                    .white};
                                  ${i === selectedCoordinates.groupIndex &&
                                  j === selectedCoordinates.elementIndex &&
                                  css`
                                    background-color: ${styleConstants.colors
                                      .gray_light};
                                  `}
                                  ${getBreakpointDown(breakpointSizes.XSM)(css`
                                    padding: 0px 10px;
                                  `)}
                                `}
                                onMouseEnter={(): void =>
                                  onOptionMouseOver(i, j)
                                }
                                onFocus={(): void => onOptionMouseOver(i, j)}
                                key={j}
                                onClick={
                                  !option?.disabled
                                    ? (e): void =>
                                        onOptionClick(option.value, e)
                                    : undefined
                                }
                                onKeyPress={(): null => {
                                  return null
                                }}
                                role="button"
                                tabIndex={0}
                              >
                                {props.isMulti && (
                                  <Checkbox
                                    id={
                                      props.id
                                        ? props.id + 'CheckBox'
                                        : 'CheckBox'
                                    }
                                    isSelected={props.selectedValues.has(
                                      option.value,
                                    )}
                                    isHighlighted={
                                      i === selectedCoordinates.groupIndex &&
                                      j === selectedCoordinates.elementIndex
                                    }
                                    css={css`
                                      margin-right: 10px;
                                    `}
                                  />
                                )}
                                {!props.isMulti && (
                                  <div
                                    css={css`
                                      width: 24px;
                                      height: 24px;
                                      display: flex;
                                      align-items: flex-start;
                                      justify-content: center;
                                      transform: scale(0.75);
                                      border-radius: 50%;
                                      margin-right: 10px;
                                      flex-shrink: 0;
                                      opacity: 0;

                                      ${props.selectedValues.has(
                                        option.value,
                                      ) &&
                                      css`
                                        opacity: 1;
                                        background-color: ${themeConstants
                                          .colors.cta};
                                      `}
                                    `}
                                  >
                                    <SvgCheckLoD
                                      css={css`
                                        display: none;
                                        ${props.selectedValues.has(
                                          option.value,
                                        ) &&
                                        css`
                                          display: block;
                                        `}
                                      `}
                                    />
                                  </div>
                                )}
                                {props.allowNoneProps !== undefined &&
                                  option.value === '' && (
                                    <props.allowNoneProps.NoneDropdownItem />
                                  )}
                                {(props.allowNoneProps === undefined ||
                                  option.value !== '') && (
                                  <props.DropdownItem
                                    isRoot={false}
                                    isSelected={
                                      !!props.selectedValues?.has(option.value)
                                    }
                                    details={option.details}
                                    value={option.value}
                                    disabled={option.disabled}
                                    chooseOption={
                                      !option.disabled
                                        ? props.chooseOption
                                        : undefined
                                    }
                                    isMulti={props.isMulti}
                                  />
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      ))}
                      {props.infiniteScrollProps?.loading && (
                        <LoadingBoxBottom />
                      )}
                    </div>
                    {props.onResetClick !== undefined && (
                      <div
                        css={css`
                          height: 50px;
                        `}
                      />
                    )}
                    {props.onResetClick !== undefined && (
                      <div
                        css={css`
                          height: 50px;
                          min-height: 50px;
                          max-height: 50px;
                          width: 100%;
                          position: absolute;
                          bottom: 0;
                          background-color: ${styleConstants.colors
                            .gray_lightest};
                          box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.15);
                          display: flex;
                          justify-content: flex-end;
                          align-items: center;
                          padding: 0px 20px;
                        `}
                      >
                        <HoverableLink
                          underline
                          onClick={(): void => {
                            props.onResetClick?.()
                            setFilterInput('')
                          }}
                          ariaLabelSource={'Reset'}
                        >
                          {translate('Reset')}
                        </HoverableLink>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {showErrors && props.errors !== undefined && (
            <ErrorList errors={props.errors} />
          )}
        </Fragment>
      )}
    </div>
  )
}

export default Dropdown
